import {
  Card,
  CardMedia,
  Typography,
  Avatar,
  Box,
  Tab,
  Tabs,
  Stack,
  IconButton,
  Grid,
  Button,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import toast from 'react-hot-toast';
import React, {
  ComponentPropsWithoutRef,
  useEffect,
  useRef,
  useState,
} from 'react';

interface DownloadLinkProps {
  video: any;
  downloadType: string;
  label: string;
  onDownload: (type: string) => void;
  brand?: any;
}

const DownloadLink: React.FC<DownloadLinkProps> = ({ video, downloadType, label, onDownload, brand }) => {
  const { role, user } = useUser();
  const isInternalUser = isInternal(role);
  
  const canDownloadUnapproved = React.useMemo(() => {
    if (isInternalUser) return true;
    
    if (isBrand(role) || isRMN(user)) {
      const userBrandIds = user?.brand?.map((b: any) => b.id || b) || [];
      
      if (brand?.pre_approval_downloading && userBrandIds.includes(brand.id)) {
        return true;
      }
      
      const hasPreApprovalEnabled = video.brandOwners?.some((brandItem: any) => 
        userBrandIds.includes(brandItem.id) && brandItem.pre_approval_downloading === true
      );
      
      if (hasPreApprovalEnabled) {
        return true;
      }
    }
    
    return false;
  }, [isInternalUser, role, user, brand, video.brandOwners]);
  
  const isVideoApproved = isInternalUser || canDownloadUnapproved || (typeof video.final === 'boolean' ? video.final : true);
  
  const handleClick = () => {
    if (!isVideoApproved) {
      toast.error("This video is not approved yet. Please wait for approval before downloading.");
      return;
    }
    onDownload(downloadType);
  };

  const styles = {
    background: 'var(--surface)',
    color: isVideoApproved ? '#4f4fef' : 'var(--muted)',
    cursor: isVideoApproved ? 'pointer' : 'not-allowed',
    fontStyle: 'italic' as const,
    opacity: isVideoApproved ? 1 : 0.5,
    ':hover': {
      color: isVideoApproved ? '#0f0fa8' : 'var(--muted)',
      textDecoration: isVideoApproved ? 'underline' : 'none',
    },
  };
  
  return (
    <Box onClick={handleClick} sx={styles}>
      {label}
    </Box>
  );
};

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useSearchParams, NavLink, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { getRenamedDownload } from '../../../../utils/downloadHelpers';
import { buildDownloadFileName, formatName as videoDisplayName } from '../selectedVideoCardV2/helpers';
import { ContentCard } from './ContentCard';
import axiosInstant from '../../../../api';
import {
  getBrandByVideoId,
  getVideoUGC,
  getVideoFile,
  getVideoEmbeds,
  removeVideoFromEmbed,
} from '../../../../api/endpoints';
import { useUser } from '../../../../hooks/useUser';
import { isInternal, isBrand, isRMN } from '../../../../utils';
import config from '../../../../config';
import { EmbedSelect } from './EmbedSelect';
import Qr from '../../../../components/Qr/Qr';
import { IVideo, formatVideoName } from '../hooks/useFetchVideos';
import { EmbedCard } from '../../embeds/components/EmbedCard';
import { CopyButton } from '../../../../components/fields/CopyButton/CopyButton';
import { VideoAnalytics } from '../../videoAnalytics';
import { SettingsForm } from './SettingsForm';
import { usePortalPath } from '../../../../hooks/usePortalPath'

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Box>{children}</Box>
        </Box>
      )}
    </div>
  );
}
TabPanel.defaultProps = {
  children: null,
};

const DEFAULT_TABS = [
  'Video',
  'Groups',
  'Embed Code',
  'Preview',
  'Thumbnails',
  'Settings',
  'Analytics',
];

interface SelectedVideoCardProps extends ComponentPropsWithoutRef<'div'> {
  videoId: number;
  type: string;
  refetch: () => void;
  onClose?: () => void;
  onClick?: () => void;
}

export function SelectedVideoCard({
  videoId,
  type,
  onClose,
  onClick,
  refetch,
}: SelectedVideoCardProps) {
  const videoRef = useRef<HTMLAnchorElement>(null);
  const [value, setValue] = useState(0);
  const [selectedEmbed, setSelectedEmbed] = useState<null | number>(null);
  const [video, setVideo] = useState<any>({});
  const [embeds, setEmbeds] = useState([]);
  const [s3id, setS3id] = useState(null);
  const [source, setSource] = useState('');
  const [downloadSource, setDownloadSource] = useState('');
  const [downloadFileName, setDownloadFileName] = useState('');
  const [addVideoDialogState, setAddVideoDialogState] = useState(false);
  const [showDownloadDialog, openDownloadDialog] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isTermChecked, setIsTermChecked] = useState(false);
  const [srt, setSrt] = useState(false);
  const [transcripts, setTranscripts] = useState<Array<{ ext: string; url: string; label: string }>>([]);
  const [thumbnails, setThumbnails] = useState<any>([]);
  const navigate = useNavigate();
  const { p } = usePortalPath();

  const { user } = useUser();
  const [brand, setBrand] = useState<any>({});

  const fetchEmbeds = async (_s3id: any) => {
    if (_s3id) {
      const response = await axiosInstant.get(getVideoEmbeds(_s3id, ''));
      const { data } = response;
      setEmbeds(data.rows);
    }
  };

  const fetchBrand = async () => {
    const endpoint: any = getBrandByVideoId(String(videoId));
    try {
      const response = await axiosInstant.get(endpoint);
      const { data } = response;
      if (data) {
        setBrand(data);
      }
    } catch (err) {
      onClose?.();
    }
  };

  const fetchVideo = async () => {
    let endpoint: any;

    switch (type) {
      case 'review':
        endpoint = getVideoUGC(brand.id, videoId);
        break;
      case 'file':
      default:
        endpoint = getVideoFile(brand.id, videoId);
        break;
    }

    try {
      const response = await axiosInstant.get(endpoint);
      const { data } = response;
      if (data) {
        setVideo(data);
        let s3Id;
        if (type === 'review') {
          s3Id = data.reviewS3File.id;
          // setS3id(data.reviewS3File.id)
        } else {
          s3Id = data.id;
          // setS3id(data.id)
        }

        setS3id(s3Id);
        fetchEmbeds(s3Id);
      }
    } catch (err) {
      onClose?.();
    }
  };

  // Change Tab
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    searchParams.set('selectedTab', newValue.toString());
    setSearchParams(searchParams);
  };

  const handleClose = () => {
    setAddVideoDialogState(false);
    searchParams.delete('selectedTab');
    setSearchParams(searchParams);
    fetchVideo();
  };

  const goToEmbed = () => {
    searchParams.set('selectedTab', '2');
    setSearchParams(searchParams);
  };

  const removeVideo = async (embedId: any) => {
    if (s3id) {
      const endpoint: any = removeVideoFromEmbed(embedId, s3id);
      const response = await axiosInstant.delete(endpoint);
      fetchVideo();
    }
  };

  useEffect(() => {
    fetchBrand();
  }, [videoId]);

  useEffect(() => {
    if (videoId && brand.id) fetchVideo();
  }, [brand, videoId]);

  useEffect(() => {
    const url = video.location || video.reviewS3File?.location;
    if (url) {
      setSource(url);
      handleSrt();
      handleThumbnails();
    }
  }, [video]);

  useEffect(() => {
    const activeTab = searchParams.get('selectedTab');
    if (activeTab) {
      setValue(parseInt(activeTab, 10));
    } else {
      setValue(0);
    }
  }, [searchParams]);

  const handleThumbnails = async () => {
    const thumbnails = [];
    const formats = ['jpg', 'tif'];
    let thumbnailExists = false;

    // Check for multiple iconik thumbnails
    for (let j = 0; j < formats.length; j++) {
      // Check for a single iconik thumbnail
      thumbnailExists = await exists(formatUrl(formats[j]));
      if (thumbnailExists) {
        thumbnails.push({
          type: formats[j],
          i: false,
          name: `${formats[j]} Thumbnail 1`,
          url: formatUrl(formats[j]),
        });
      }

      let found = true;
      let i = 1;
      while (found == true) {
        thumbnailExists = await exists(formatUrl(formats[j], i));
        if (thumbnailExists) {
          thumbnails.push({
            type: formats[j],
            i,
            name: `${formats[j]} Thumbnail ${i + 1}`,
            url: formatUrl(formats[j]),
          });
        } else {
          found = false;
        }
        i++;
      }
    }

    // Get generated thumbnail .png
    thumbnailExists = await exists(formatUrl('png'));
    if (thumbnailExists) {
      thumbnails.push({
        type: 'png',
        i: false,
        name: 'Auto Generated .png Thumbnail',
        url: formatUrl('png'),
      });
    }

    // Get generated thumbnail w/ playicon .png
    thumbnailExists = await exists(formatUrl('playicon'));
    if (thumbnailExists) {
      thumbnails.push({
        type: 'playicon',
        i: false,
        name: 'Auto Generated .png Play Icon Thumbnail',
        url: formatUrl('playicon'),
      });
    }

    // Iconik drag-and-drop thumbnails: hash-v{version}-{i}.{ext} under /thumbnails/
    const iconikCount = (video as any).thumbnail_count || 0;
    if (iconikCount > 0 && video.hash) {
      const baseUrl = video.location || video.reviewS3File?.location;
      const version = Number(video.version) || 1;
      if (baseUrl) {
        const lastSlash = baseUrl.lastIndexOf('/');
        const dir = baseUrl.substring(0, lastSlash);
        for (let n = 1; n <= iconikCount; n++) {
          for (const ext of ['jpg', 'png', 'tiff'] as const) {
            const url = `${dir}/thumbnails/${video.hash}-v${version}-${n}.${ext}`;
            // eslint-disable-next-line no-await-in-loop
            if (await exists(url)) {
              thumbnails.push({
                type: 'iconik',
                i: n,
                name: `Thumbnail ${n} .${ext}`,
                url,
                directUrl: true,
              });
              break;
            }
          }
        }
      }
    }

    setThumbnails(thumbnails);
  };

  const handleSrt = async () => {
    const srtExists = await exists(formatUrl('srt'));
    setSrt(srtExists);

    // Auto-transcribed: `{hash}_v{version}_transcript.{srt,vtt}` alongside the video file
    const baseUrl = video.location || video.reviewS3File?.location;
    const hash = video.hash;
    const version = Number(video.version) || 1;
    if (!baseUrl || !hash) {
      setTranscripts([]);
      return;
    }

    const dir = baseUrl.substring(0, baseUrl.lastIndexOf('/'));
    const autoExts = ['srt', 'vtt'] as const;
    const results = await Promise.all(
      autoExts.map(async (ext) => {
        const url = `${dir}/${hash}_v${version}_transcript.${ext}`;
        return (await exists(url)) ? { ext, url, label: `Download Transcript .${ext}` } : null;
      }),
    );
    setTranscripts(results.filter(Boolean) as Array<{ ext: string; url: string; label: string }>);
  };

  const formatName = (videoObject: IVideo) => {
    if (videoObject.name) return videoObject.name;
    return formatVideoName(String(videoObject.key || videoObject.title || ''));
  };

  const formatAuthor = (videoObject: IVideo) => {
    const name = videoObject.author?.first_name || '';
    return name;
  };

  const formatEmbedCode = () => `<script class="tdc2-single" bid="${brand.id}" b="${brand.slug}" fid="${s3id}" type="text/javascript" src="${config.EMBED_HOST}/assets/js/e2s.js?source=embed"></script>`;

  const formatLandingUrl = (source: string = 'landing', preview = false) => {
    let previewString = '';
    if (preview) {
      previewString = '&preview=true';
    }
    return `${config.EMBED_HOST}/brands/${brand.id}/${brand.slug}/single/${s3id}?source=${source}${previewString}`;
  };

  const updateVideoPage = () => {
    refetch();
    fetchVideo();
  };

  const formatWebM = (s: any) => {
    const ext = `.${  s?.substr(s.lastIndexOf('.') + 1)}`;
    return (s = s?.replace(ext, `_optimized.webm`));
  };

  const formatWebOptimized = (s: any) => {
    const ext = `.${  s?.substr(s.lastIndexOf('.') + 1)}`;
    return (s = s?.replace(ext, `_optimized${ext}`));
  };

  const formatHighResolution = (s: any) => {
    const ext = `.${  s?.substr(s.lastIndexOf('.') + 1)}`;
    return (s = s?.replace(ext, `_original${ext}`));
  };

  const formatPngThumbnail = (s: any) => {
    const ext = `.${  s?.substr(s.lastIndexOf('.') + 1)}`;
    return (s = s?.replace(ext, '_thumbnail.png'));
  };

  const formatPlayIconThumbnail = (s: any) => {
    const ext = `.${  s?.substr(s.lastIndexOf('.') + 1)}`;
    return (s = s?.replace(ext, '_thumbnail_play_icon.png'));
  };

  const formatSrt = (s: any) => {
    s = s.substr(0, s.lastIndexOf('_'));
    s = s.replace('tdc', 'tdc/cc');
    s += '.mp4-transcription.srt';
    return s;
  };

  const formatJpg = (s: any, i: any = 0) => {
    const ext = '.jpg';
    s = s.substr(0, s.lastIndexOf('_'));
    s = s.replace('tdc', 'tdc/thumbnails');
    if (i) {
      s = `${s  }-${  i}`;
    }
    s += ext;
    return s;
  };

  const formatTif = (s: any, i: any = 0) => {
    const ext = '.tif';
    s = s.substr(0, s.lastIndexOf('_'));
    s = s.replace('tdc', 'tdc/thumbnails');
    if (i) {
      s = `${s  }-${  i}`;
    }
    s += ext;
    return s;
  };

  const exists = async (url: any) => {
    const ms = Date.now();
    // url = url+'?t='+ms
    try {
      const result = await fetch(url, { method: 'HEAD' });
      return result.ok;
    } catch (err) {
      return false;
    }
  };

  const formatUrl = (type: any, i: any = false) => {
    let url = video.location || video.reviewS3File?.location;
    switch (type) {
      case 'webm':
        url = formatWebM(url);
        break;
      case 'web':
        url = formatWebOptimized(url);
        break;
      case 'high':
        url = formatHighResolution(url);
        break;
      case 'png':
        url = formatPngThumbnail(url);
        break;
      case 'playicon':
        url = formatPlayIconThumbnail(url);
        break;
      case 'srt':
        url = formatSrt(url);
        break;
      case 'jpg':
        url = formatJpg(url, i);
        break;
      case 'tif':
        url = formatTif(url, i);
        break;
    }
    return url;
  };

  // Presign like every other download surface so the file saves under the
  // same friendly name; the raw URL stays as the fallback.
  const prepareDownload = async (url: string) => {
    openDownloadDialog(true);
    setDownloadSource(url);
    setDownloadFileName('');
    let key = '';
    try {
      key = decodeURIComponent(new URL(url).pathname.replace(/^\//, ''));
    } catch {
      key = '';
    }
    const { link, fileName } = await getRenamedDownload({
      bucket: video.bucket,
      key,
      fileName: buildDownloadFileName(key || url, videoDisplayName(video), video),
      fallbackUrl: url,
    });
    setDownloadSource(link);
    setDownloadFileName(fileName);
  };

  const openDownloadDialogType = (type: any, i: any = false) => prepareDownload(formatUrl(type, i));

  const openDownloadDialogUrl = (url: string) => prepareDownload(url);

  return (
    <Card>
      <Stack
        direction="row"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          {DEFAULT_TABS.map((tab, index) => (
            <Tab label={tab} key={index} />
          ))}
        </Tabs>

        <Box marginRight={2}>
          <IconButton size="small" onClick={onClose}>
            <CancelOutlinedIcon />
          </IconButton>
        </Box>
      </Stack>

      {/* Video */}
      <TabPanel value={value} index={0}>
        <CardMedia
          component="video"
          controls
          src={formatWebM(source)}
          poster={formatPlayIconThumbnail(source)}
          style={{ 
            maxHeight: '300px',
            maxWidth: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain'
          }}
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography
              gutterBottom
              component="div"
              style={{ marginTop: '10px' }}
              fontWeight={700}
              fontSize={18}
            >
              {formatName(video)}
            </Typography>
            <Typography
              fontWeight={400}
              fontSize={14}
              color="text.secondary"
              display="flex"
              gap={1}
              alignItems="center"
            >
              {video.rating && (
                <Rating
                  name="read-only"
                  value={video.rating}
                  precision={0.5}
                  readOnly
                />
              )}
              <DownloadLink 
                video={video} 
                downloadType="web" 
                label="Download HD Web Optimized Version"
                onDownload={openDownloadDialogType}
                brand={brand}
              />
              |
              <DownloadLink 
                video={video} 
                downloadType="high" 
                label="Download HD Original Version"
                onDownload={openDownloadDialogType}
                brand={brand}
              />
              {srt && (
                <>
                  |
                  <Box
                    onClick={() => openDownloadDialogType('srt')}
                    sx={{
                      background: 'var(--surface)',
                      color: '#4f4fef',
                      cursor: 'pointer',
                      fontStyle: 'italic',
                      ':hover': {
                        color: '#0f0fa8',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Download CC .srt
                  </Box>
                </>
              )}
              {transcripts.map((t) => (
                <React.Fragment key={t.ext}>
                  |
                  <Box
                    onClick={() => openDownloadDialogUrl(t.url)}
                    sx={{
                      background: 'var(--surface)',
                      color: '#4f4fef',
                      cursor: 'pointer',
                      fontStyle: 'italic',
                      ':hover': {
                        color: '#0f0fa8',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {t.label}
                  </Box>
                </React.Fragment>
              ))}
            </Typography>
            <Typography fontWeight={400} fontSize={14} color="text.secondary">
              {video.review_text}
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center">
            {video.author && (
              <>
                <Avatar sx={{ width: 34, height: 34 }}>
                  {formatAuthor(video).charAt(0)}
                </Avatar>
                <Typography fontSize={14} fontWeight={700} marginLeft={1}>
                  {formatAuthor(video)}
                </Typography>
              </>
            )}
          </Stack>
        </Stack>
      </TabPanel>

      {/* Embeds */}
      <TabPanel value={value} index={1}>
        <Stack direction="row" alignItems="right" justifyContent="right">
          <Button
            onClick={() => {
              setAddVideoDialogState(true);
            }}
            size="large"
            type="submit"
            variant="outlined"
            disableElevation
            style={{ marginBottom: '20px' }}
            startIcon={<AddIcon />}
          >
            Add Video To Another Group
          </Button>
        </Stack>
        <Grid container spacing={2} columns={{ xs: 12 }}>
          {/* <Grid item xs={4} key={0} onClick={goToEmbed}>
            <EmbedCard>
              <Typography fontWeight="bold">Single Video Embed</Typography>
            </EmbedCard>
          </Grid> */}
          {embeds.map((embed: any, i) => (
            <Grid item xs={4} key={i}>
              <EmbedCard
                onClick={() => navigate(p(`/videos/embeds?embed=${embed.id}`))}
              >
                <Typography fontWeight="bold">{embed.name}</Typography>
                <Button
                  onClick={() => {
                    removeVideo(embed.id);
                  }}
                  size="small"
                  type="submit"
                  variant="contained"
                  disableElevation
                  style={{ marginTop: '10px' }}
                >
                  Remove
                </Button>
              </EmbedCard>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Embed Code */}
      <TabPanel value={value} index={2}>
        <Box>
          <Typography fontWeight="bold">
            Solo Video Player Embed Code
          </Typography>

          <Box
            padding={2}
            borderRadius={2.25}
            sx={{
              backgroundColor: '#5B482A',
              color: '#ffffff',
              fontWeight: 300,
            }}
            marginY={2}
          >
            {/* @ts-ignore  */}
            <code>
              {/* @ts-ignore  */}
              {/* <script class="tdc" pid="8217" brand="leanrite" type="text/javascript" src="https://thedesirecompany.com/assets/js/e.js?bgColor=ffffff&amp;proColor=e01522&amp;navigationColor=e01522&amp;annotations={'text':'Add To Cart', 'textColor':'ffffff', 'color':'e01522', 'targetClass':'add_to_cart','startTime':2,'endTime':7,'borderRadius':'0px'}"></script> */}
              {/* prettier-ignore */}
              {/* &lt;script class=&quot;tdc&quot; pid=&quot;8217&quot; brand=&quot;leanrite&quot; type=&quot;text/javascript&quot; src=&quot;https://thedesirecompany.com/assets/js/e2.js?bgColor=ffffff&amp;proColor=e01522&amp;amp;navigationColor=e01522&amp;annotations=&#123;&apos;text&apos;:&apos;Add To Cart&apos;, &apos;textColor&apos;:&apos;ffffff&apos;, &apos;color&apos;:&apos;e01522&apos;, &apos;targetClass&apos;:&apos;add_to_cart&apos;,&apos;startTime&apos;:2,&apos;endTime&apos;:7,&apos;borderRadius&apos;:&apos;0px&apos;&#125;&quot;&gt;&lt;/script&gt; */}
              {/* &lt;script class=&quot;tdc&quot; bid=&quot;{brandId}&quot; fid=&quot;{video.id}&quot; type=&quot;text/javascript&quot; src=&quot;{config.EMBED_HOST}/assets/js/e2.js&quot;&gt;&lt;/script&gt; */}
              {/* &lt;script class=&quot;tdc2-single&quot; bid=&quot;{brandId}&quot; fid=&quot;{s3id}&quot; type=&quot;text/javascript&quot; src=&quot;
              {config.EMBED_HOST}/assets/js/e2s.js&quot;&gt;&lt;/script&gt; */}
              {formatEmbedCode()}
            </code>
          </Box>

          <Stack direction="row" spacing={2}>
            <CopyButton
              value={formatEmbedCode()}
              variant="outlined"
              color="primary"
            >
              Copy Embed Code
            </CopyButton>
            {/* <Button variant="contained" color="success" disableElevation>
              Live Demo
            </Button> */}
          </Stack>
        </Box>
      </TabPanel>

      {/* Single Preview Page Code */}
      <TabPanel value={value} index={3}>
        <Grid container spacing={2} columns={{ xs: 12 }}>
          <Grid item xs={6} key={1}>
            <ContentCard style={{ textAlign: 'center' }}>
              <Typography fontWeight="bold" textAlign="center">
                Solo Video Player Page
              </Typography>
              <div style={{ width: '50%', margin: '0px auto' }}>
                <Typography noWrap style={{ fontSize: '0.7rem' }}>
                  <strong>Share URL:</strong>{' '}
                  <NavLink
                    target="_blank"
                    to={formatLandingUrl('landing', false)}
                  >
                    {formatLandingUrl('landing', false)}
                  </NavLink>
                </Typography>
                <Typography noWrap style={{ fontSize: '0.7rem' }}>
                  <strong>Preview URL:</strong>{' '}
                  <NavLink
                    target="_blank"
                    to={formatLandingUrl('landing', true)}
                  >
                    {formatLandingUrl('landing', true)}
                  </NavLink>
                </Typography>
              </div>
              <Qr url={formatLandingUrl('qr')} />
              <NavLink target="_blank" to={formatLandingUrl('landing', true)}>
                <Button
                  style={{ marginTop: '-10px', marginLeft: '5px' }}
                  size="small"
                  variant="outlined"
                  color="primary"
                >
                  Preview
                </Button>
              </NavLink>
              <CopyButton
                text="Share Url"
                value={formatLandingUrl('landing')}
                style={{ marginTop: '-10px', marginLeft: '5px' }}
                size="small"
                variant=""
                color="primary"
               />
            </ContentCard>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Thumbnails */}
      <TabPanel value={value} index={4}>
        <Grid container spacing={2} columns={{ xs: 12 }}>
          {/* <Grid item xs={4} key={0} onClick={goToEmbed}>
            <EmbedCard>
              <Typography fontWeight="bold">Single Video Embed</Typography>
            </EmbedCard>
          </Grid> */}
          {thumbnails.map((thumbnail: any, i: any) => (
            <Grid item xs={4} key={i}>
              <EmbedCard
                image={thumbnail.url}
                onClick={() =>
                  thumbnail.directUrl
                    ? openDownloadDialogUrl(thumbnail.url)
                    : openDownloadDialogType(thumbnail.type, thumbnail.i)
                }
              >
                <Typography fontWeight="bold">{thumbnail.name}</Typography>
                {/* <Button
                  onClick={() => {
                    removeVideo(embed.id)
                  }}
                  size="small"
                  type="submit"
                  variant="contained"
                  disableElevation
                  style={{ marginTop: '10px' }}
                >
                  Remove
                </Button> */}
              </EmbedCard>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Settings */}
      <TabPanel value={value} index={5}>
        <SettingsForm
          videoId={video.id}
          brandId={brand.id}
          name={formatName(video)}
          published_public={video.published_public}
          onComplete={updateVideoPage}
        />
      </TabPanel>

      {/* Analytics */}
      <TabPanel value={value} index={6}>
        <VideoAnalytics
          brandId={brand.id}
          videoId={type === 'review' ? video.reviewS3File.id : video.id}
        />
      </TabPanel>

      {/* Pop up  */}
      <Dialog
        open={addVideoDialogState}
        fullWidth
        maxWidth="md"
        onClose={() => handleClose()}
      >
        <DialogTitle>Add Video To Group</DialogTitle>
        <DialogContent>
          <EmbedSelect
            brandId={brand.id}
            videoId={s3id}
            onClose={() => handleClose()}
          />
          {/* <EmbedForm brandId={brandId} onComplete={() => onComplete() } /> */}
        </DialogContent>
      </Dialog>

      {/* Pop up  */}
      <Dialog
        open={showDownloadDialog}
        maxWidth="md"
        onClose={() => {
          openDownloadDialog(false);
          setIsTermChecked(false);
        }}
      >
        <DialogTitle variant="h4" color="var(--accent-text)">
          Terms & Conditions
        </DialogTitle>
        <DialogContent>
          <Stack alignItems="center" justifyContent="center">
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e) => setIsTermChecked(e.target.checked)}
                />
              }
              label="I agree to the licensing agreement with The Desire Company for this media."
            />
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content */}
            <a href={downloadSource} download={downloadFileName || true} ref={videoRef} />
            <Button
              onClick={() => {
                videoRef.current?.click();
                openDownloadDialog(false);
              }}
              variant="contained"
              color="primary"
              disabled={!isTermChecked}
              sx={{
                marginTop: 3,
                width: '100%',
                textAlign: 'right',
              }}
            >
              Proceed to download
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

SelectedVideoCard.defaultProps = {
  onClick: () => {},
  onClose: () => {},
};
